import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { FormInstance } from "antd";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import prettyBytes from "pretty-bytes";
import {
  GET_COMPANY_SETTINGS_CENTRAL_API,
  LIST_PLATFORMS,
  UPDATE_COMPANY_SETTINGS_CENTRAL_API,
} from "queries/centralApi/companies";
import { EXPORT, GET_EXPORTS } from "queries/centralApi/vats";
import { useEffect, useMemo, useState } from "react";
import useCoreStore from "views/hooks/useCoreStore";
import useSettingsStore from "views/hooks/useSettingsStore";

interface DataTable {
  key: number;
  id: ID;
  exportId: ID;
  fileName: string;
  createdAt: string;
  createdBy: string;
  bookEntriesSource: string;
  created: string;
  canBeReexported: boolean;
  autoExported: boolean;
  reexportedAt: boolean;
  size: string;
  uploadedToInterops: boolean;
  totalDocuments: number;
  url: string;
}

interface PlatformsOptionsStateType {
  label: string;
  value: ID;
  name: string;
  software: string;
  defaultPrefixClientAccount: string;
}

export default function useAccountingApi({ form }: { form?: FormInstance } = {}) {
  const setCompanyAccountingSettings = useCoreStore(state => state.setCompanyAccountingSettings);
  const setCanExport = useSettingsStore(state => state.setCanExport);

  const [dataSource, setDataSource] = useState<ListVatsResults>([]);
  const [refetchTable] = useState<boolean>(false);

  const [
    listCompanyExports,
    {
      refetch: refetchListCompanyExports,
      loading: listCompanyExportsLoading,
      data: listCompanyExportsData,
      error: listCompanyExportsError,
    },
  ] = useLazyQuery<companyLastExportsOutput, companyLastExportsInput>(GET_EXPORTS, {
    client: clientCentralApiStrawberry,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (listCompanyExportsError) onErrorGraphql(listCompanyExportsError);
  }, [listCompanyExportsError]);

  const listExportsData: DataTable[] = useMemo(
    () =>
      listCompanyExportsData?.listCompanyExports?.map((item, index) => {
        const date = dayjs(item.created).format(DATE_FORMAT);
        const time = dayjs(item.created).format("HH:mm");
        const timeWithSeconds = dayjs(item.created).format("HH:mm:ss");
        return {
          key: index,
          id: `${date}-${timeWithSeconds}`,
          date,
          time,
          url: item.url,
          exportId: item.id,
          fileName: item.fileName,
          createdAt: item.createdAt,
          createdBy: item.createdBy,
          created: item.created,
          canBeReexported: item.canBeReexported,
          bookEntriesSource: item.bookEntriesSource,
          autoExported: item.autoExported,
          reexportedAt: item.reexportedAt,
          totalDocuments: item.totalDocuments,
          minEntryDate: item.minEntryDate,
          maxEntryDate: item.maxEntryDate,
          uploadedToInterops: item.uploadedToInterops,
          size: item.size && prettyBytes(Number(item.size)),
        } as DataTable;
      }) ?? [],
    [listCompanyExportsData],
  );

  const [exportPlatform, { loading: exportPlatformLoading }] = useMutation<
    ExportPlatformOutput,
    ExportPlatformInput
  >(EXPORT, {
    client: clientCentralApiStrawberry,
    fetchPolicy: "no-cache",
  });

  const [listPlatforms, { data: listPlatformsData, error: listPlatformsError }] = useLazyQuery<
    ListPlatformsOutput,
    ListPlatformsInput
  >(LIST_PLATFORMS, {
    client: clientCentralApiStrawberry,
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (listPlatformsError) onErrorGraphql(listPlatformsError);
  }, [listPlatformsError]);

  const platformsOptions: PlatformsOptionsStateType[] = useMemo(
    () =>
      listPlatformsData?.listPlatforms?.results?.map(item => ({
        label: `${item.name} - ${item.software?.value ?? ""}`,
        value: item.id,
        name: item.name,
        software: item.software?.value ?? "",
        defaultPrefixClientAccount: item.defaultPrefixClientAccount,
      })) ?? [],
    [listPlatformsData],
  );

  const [
    getCompanyPlatform,
    {
      refetch: refetchCompanyPlatform,
      loading: getCompanyPlatformLoading,
      data: getCompanyPlatformData,
      error: getCompanyPlatformError,
    },
  ] = useLazyQuery<GetCompanyPlatformOutput, GetVarTypeID>(GET_COMPANY_SETTINGS_CENTRAL_API, {
    client: clientCentralApiStrawberry,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getCompanyPlatformError) onErrorGraphql(getCompanyPlatformError);
  }, [getCompanyPlatformError]);

  const platformData: PlatformData | undefined = getCompanyPlatformData?.getCompanySettings;

  useEffect(() => {
    const getCompanySettings = getCompanyPlatformData?.getCompanySettings;
    if (!getCompanySettings) return;

    form?.setFieldsValue({
      canExport: getCompanySettings?.canExport,
      generalAccountsLength: getCompanySettings?.generalAccountsLength,
      thirdPartiesAccountsLength: getCompanySettings?.thirdPartiesAccountsLength,
      collectiveClientAccount:
        getCompanySettings?.collectiveClientAccount ||
        getCompanySettings?.software?.collectiveClientAccount,
      collectiveProviderAccount:
        getCompanySettings?.collectiveProviderAccount ||
        getCompanySettings?.software?.collectiveProviderAccount,
      prefixProviderAccount:
        getCompanySettings?.prefixProviderAccount ||
        getCompanySettings?.software?.defaultPrefixProviderAccount,
      prefixClientAccount:
        getCompanySettings?.prefixClientAccount ||
        getCompanySettings?.software?.defaultPrefixClientAccount,
      allowAccountCreationsInSsi: getCompanySettings?.allowAccountCreationsInSsi,
      ssiType: getCompanySettings?.ssiType?.value,
    });

    setCompanyAccountingSettings(getCompanySettings);
    setCanExport(getCompanySettings?.canExport);
  }, [getCompanyPlatformData, form]);

  const [updateCompanySettingsCentralApi, { loading: updateCompanySettingsCentralApiLoading }] =
    useMutation<UpdateCompanySettingsCentralApiOutput, UpdateCompanySettingsCentralApiInput>(
      UPDATE_COMPANY_SETTINGS_CENTRAL_API,
      {
        client: clientCentralApiStrawberry,
      },
    );

  return {
    dataSource,
    refetchTable,
    platformData,
    setDataSource,
    listPlatforms,
    exportPlatform,
    listExportsData,
    platformsOptions,
    getCompanyPlatform,
    listCompanyExports,
    exportPlatformLoading,
    refetchCompanyPlatform,
    refetchListCompanyExports,
    getCompanyPlatformLoading,
    listCompanyExportsLoading,
    updateCompanySettingsCentralApi,
    updateCompanySettingsCentralApiLoading,
  };
}
